import { FC, useCallback } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ModalDialog } from 'components/ModalDialog';
import { PanelWithFields } from 'modules/core/components';
import { EMPTY_STRING } from 'modules/core/constants';
import { MODAL_DIALOG_ID, MODAL_DIALOG_WIDTH } from 'modules/core/constants/modalDialog.const';
import { TableName } from 'modules/core/enums';
import { useModalState } from 'modules/core/hooks/useModalState';
import {
  FEES_AND_TAXES_INDEX_FIELD,
  FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES,
  FRONT_AND_BACK_ENDS_ADDS_INDEX_FIELD,
} from 'modules/RACAffordabilityEngine/const/fieldNames.const';
import { PREFERENCES_INDEX_FIELD } from 'modules/core/shared';
import { getNormalizedDirty } from 'components/Modals/utils';
import { ESTIMATED_FEES_OVERRIDDEN } from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';

import { IProbabilityEstimatorForm } from '../types';
import { LenderSettingsContent } from './components/LenderSettingsContent';
import { useSettingsModalFields } from './hooks/useSettingsModalFields';
import { useVscGapValidation } from './hooks/useVscGapValidation';

interface ISettingsModal {
  tableName: TableName;
  onClose: VoidFunction;
}

export const SettingsModal: FC<ISettingsModal> = ({ tableName, onClose }) => {
  const { t } = useTranslation();
  const { getValues: commonGetValues, setValue: commonSetValue } = useFormContext<IProbabilityEstimatorForm>();

  const defaultFormValues = {
    feesAndTaxes: commonGetValues().feesAndTaxes,
    preferences: commonGetValues().preferences,
    totalFrontAndBackEndsAddsDialog: commonGetValues().totalFrontAndBackEndsAddsDialog,
  };
  const formMethods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: defaultFormValues,
  });
  const { getValues, watch } = formMethods;

  const normalizedDirty = getNormalizedDirty(formMethods, defaultFormValues);

  const { preferencesFields, taxesFeesFields, lenderFeesFields } = useSettingsModalFields(tableName);

  const inputData = watch();
  const vsc = inputData?.totalFrontAndBackEndsAddsDialog?.vsc ?? 0;
  const gap = inputData?.totalFrontAndBackEndsAddsDialog?.gap ?? 0;

  const { getVscOrGapLimit, isVscInvalid, isGapInvalid, isDisabled } = useVscGapValidation(tableName, vsc, gap);

  const backAddsLimit = getVscOrGapLimit(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC);
  const gapLimit = getVscOrGapLimit(FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP);

  const { isModalDialogActive, handleCloseDialog } = useModalState<IProbabilityEstimatorForm>([
    MODAL_DIALOG_ID.PREFERENCES,
  ]);
  const areRequiredFieldsFilledIn = Boolean(
    !Number.isNaN(inputData.feesAndTaxes?.salesTaxRate ?? NaN) &&
      !Number.isNaN(inputData.preferences?.ltvHighLimit ?? NaN),
  );

  const isLenderTable = tableName === TableName.LENDER;

  const isSaveDisabled = isLenderTable
    ? !areRequiredFieldsFilledIn || isVscInvalid || isGapInvalid
    : !areRequiredFieldsFilledIn;

  const onSubmit = useCallback(() => {
    if (areRequiredFieldsFilledIn) {
      const values = getValues();

      commonSetValue(FEES_AND_TAXES_INDEX_FIELD, values.feesAndTaxes);
      commonSetValue(PREFERENCES_INDEX_FIELD, values.preferences);
      commonSetValue(FRONT_AND_BACK_ENDS_ADDS_INDEX_FIELD, values.totalFrontAndBackEndsAddsDialog);

      if (isLenderTable && defaultFormValues.feesAndTaxes.estimatedFees !== values.feesAndTaxes.estimatedFees) {
        commonSetValue(ESTIMATED_FEES_OVERRIDDEN, true);
      }

      handleCloseDialog()();
      onClose();
    }
  }, [
    areRequiredFieldsFilledIn,
    onClose,
    handleCloseDialog,
    getValues,
    commonSetValue,
    isLenderTable,
    defaultFormValues.feesAndTaxes.estimatedFees,
  ]);

  const modalContent = isLenderTable ? (
    <LenderSettingsContent
      backAddsLimit={backAddsLimit}
      gapLimit={gapLimit}
      isDisabled={isDisabled}
      fieldsConfig={[...lenderFeesFields, ...preferencesFields]}
    />
  ) : (
    <PanelWithFields title={EMPTY_STRING} fieldsConfig={[...taxesFeesFields, ...preferencesFields]} />
  );

  return (
    <FormProvider {...formMethods}>
      <ModalDialog
        title={t('components.preferences.advancedSearch').toUpperCase()}
        submitButtonTitle={t('common.buttons.save')}
        cancelButtonTitle={t('common.buttons.cancel')}
        show={isModalDialogActive}
        onHide={() => {
          handleCloseDialog()();
          onClose();
        }}
        onSubmitModal={onSubmit}
        modalSize={MODAL_DIALOG_WIDTH.SM}
        isDisabled={isSaveDisabled}
        isDirty={normalizedDirty}
      >
        {modalContent}
      </ModalDialog>
    </FormProvider>
  );
};
